/**
 * The wizard keeps what each step holds while another step shows: only the current step is drawn
 * (so each control exists once), and a step's fields and state live here, per wizard visit. The
 * store is observable, so a step drawn again after an answer landed while it was away (or while
 * it was away and back) reads the answer. Without a provider (a step drawn alone) the step keeps
 * a store of its own.
 */
import { createContext, useCallback, useContext, useRef, useSyncExternalStore } from 'react';

export class Drafts {
  private readonly map = new Map<string, unknown>();
  private readonly listeners = new Set<() => void>();

  has(key: string): boolean {
    return this.map.has(key);
  }

  get<T>(key: string, initial: T): T {
    return this.map.has(key) ? (this.map.get(key) as T) : initial;
  }

  set(key: string, value: unknown): void {
    this.map.set(key, value);
    for (const listener of [...this.listeners]) listener();
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
}

export const DraftsContext = createContext<Drafts | null>(null);

/** The wizard's store, or one of the step's own when it is drawn alone. */
export function useDraftStore(): Drafts {
  const shared = useContext(DraftsContext);
  const own = useRef<Drafts | null>(null);
  if (shared !== null) return shared;
  own.current ??= new Drafts();
  return own.current;
}

export function useDraft<T>(key: string, initial: T): readonly [T, (next: T) => void] {
  const store = useDraftStore();
  const first = useRef(initial);
  const value = useSyncExternalStore(
    store.subscribe,
    () => store.get(key, first.current),
    () => first.current,
  );
  const set = useCallback((next: T) => store.set(key, next), [store, key]);
  return [value, set];
}
